// ─────────────────────────────────────────────────────────────────────────────
// SDD 2026-08-17 campanas-admin-alcance-consistencia · Tarea 5 — utilidades de ALCANCE de OU
// compartidas entre los dos wizards de Reconocimiento que restringen su árbol de OUs al alcance
// delegado del admin (`recognitionOuScope`): el wizard de Campañas (`AdminCampaigns.tsx`) y el
// wizard de excepciones "Campañas por Localidad" (`CampaignExceptions.tsx`). Extraídas de
// `AdminCampaigns.tsx` (donde nacieron, WP-01/RF-081) para que el segundo consumidor no las duplique.
// ─────────────────────────────────────────────────────────────────────────────
import type { OUItem, OuScope } from '@/api/types';

/**
 * WP-01 (RF-069) — expande una selección COLAPSADA del árbol (el `OuTreeSelect` guarda solo el id del
 * padre) a él MISMO + todos sus descendientes. El backend hace match literal `AnyEq(Alcance, ouId)` sin
 * expandir subárbol, así que si guardáramos solo el padre, una campaña/excepción con alcance a nivel
 * región NO alcanzaría a los usuarios de OUs hijas (ni las exclusiones los excluirían). Expandir al
 * guardar deja el alcance como el conjunto real de OUs, y el matcher del backend funciona sin cambios.
 * (Es una FOTO: OUs nuevas creadas luego bajo ese padre no entran hasta re-guardar.)
 */
export const expandOuIds = (selected: string[], items: OUItem[]): string[] => {
  const childrenOf = new Map<string, string[]>();
  for (const it of items) {
    const p = it.parentOuId ?? '';
    (childrenOf.get(p) ?? childrenOf.set(p, []).get(p)!).push(it.id);
  }
  const out = new Set<string>();
  const stack = [...selected];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const child of childrenOf.get(id) ?? []) stack.push(child);
  }
  return [...out];
};

/**
 * Ids de OU que el admin alcanza, o `null` si NO tiene restricción (Admin General / Admin
 * Reconocimiento / marca «acceso a todas las OUs»). Se expande sobre el catálogo VIGENTE, que es lo
 * mismo que hace el árbol del wizard: una sola definición para la lista y para el formulario.
 */
export const idsEnAlcance = (scope: OuScope | undefined, items: OUItem[]): Set<string> | null =>
  scope && !scope.allOusAccess ? new Set(expandOuIds([...scope.rootOuIds], items)) : null;

/**
 * ¿El registro (campaña o excepción) incluye alguna OU fuera del alcance del admin?
 *
 * Si la respuesta es sí, ese admin NO puede guardarlo: el backend valida el alcance COMPLETO
 * (`CampaignsController.RequireScope`) y responde 403 «incluye unidades organizacionales fuera de su
 * alcance» aunque solo se hayan tocado otros campos. Ofrecer «Editar»/mostrar un árbol filtrado ahí es
 * una trampa —se rellena el formulario entero y se recibe el rechazo al final—, así que la acción se
 * deshabilita (o el árbol se muestra completo, sin filtrar) y se dice por qué.
 *
 * No se relaja el backend a propósito: validar solo las OUs AÑADIDAS dejaría a un admin de OU editar
 * (y recortar) el alcance de registros que no son suyos, que es peor que la trampa que cierra.
 */
export const campanaFueraDeAlcance = (
  alcance: readonly string[],
  idsPermitidos: ReadonlySet<string> | null,
): boolean => idsPermitidos != null && alcance.some((id) => !idsPermitidos.has(id));

/**
 * Inverso de `expandOuIds`: colapsa una selección YA EXPANDIDA (lo que se persiste) de vuelta a sus
 * nodos "raíz" — los que el admin realmente eligió. Un id se descarta si su padre TAMBIÉN está en el
 * conjunto (fue arrastrado por la expansión); dos ramas sin relación padre/hijo se conservan las dos.
 *
 * Se usa SOLO para mostrar (tabla, resumen del wizard, detalle de aprobación) — nunca para guardar:
 * lo persistido sigue siendo la lista expandida completa (ver comentario de `expandOuIds`).
 */
export const collapseOuIds = (expanded: string[], items: OUItem[]): string[] => {
  const set = new Set(expanded);
  const parentOf = new Map(items.map((it) => [it.id, it.parentOuId ?? null]));
  return expanded.filter((id) => {
    const parent = parentOf.get(id);
    return !parent || !set.has(parent);
  });
};
